'use client';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useVersionPoll } from '@/helper/useVersionPoll';
import React from 'react';

// Top-of-viewport banner shown when the version poll detects that a
// newer build of this UI has been deployed while the session was
// running the old one. Rendered in `AppProviders` so it sits above
// every route including authentication.
//
// Deliberately non-dismissable: the event is rare (once per deploy,
// if that) and a reload is a single tap. A dismiss affordance would
// just train operators to swipe it away and keep running stale.
//
// z-index above MUI's modal layer (1300) so it remains visible even
// when a dialog is open — a reload requirement is strictly more
// important than whatever the user was about to confirm.
//
// Height mirrors the PrimaryAppBar at every breakpoint (56px xs, 64px sm+)
// so the banner reads as a contextual header rather than a floating overlay.

const VERSION_ENDPOINT = '/api/version';

export default function UpdateAvailableBanner(): React.JSX.Element | null {
  const { updateAvailable } = useVersionPoll(VERSION_ENDPOINT);

  if (!updateAvailable) {
    return null;
  }

  return (
    <Box
      onClick={() => {
        window.location.reload();
      }}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1400,
        width: '100%',
        minHeight: { xs: 56, sm: 64 },
        backgroundColor: 'warning.main',
        color: 'common.white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        px: 2,
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <WarningAmberRounded />
      <Typography variant="body1" fontWeight="bold" color="inherit">
        New version available — tap to reload
      </Typography>
    </Box>
  );
}
